import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../App';
import { featuredFactories } from '../data/mockData';
import { hotProducts } from '../data/mockData';

const SupplierProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openContactForm, addToCart, showNotification, toggleFavorite, isFavorite } = useAppContext();

  const supplier = featuredFactories.find(f => f.id === parseInt(id));

  if (!supplier) {
    return (
      <div className="not-found-page">
        <div className="container">
          <div className="not-found-content">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M16 16s-1.5-2-4-2-4 2-4 2" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            <h1>Supplier Not Found</h1>
            <p>The supplier you're looking for doesn't exist or has been removed.</p>
            <button className="btn btn-primary" onClick={() => navigate('/suppliers')}>
              Back to Suppliers
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get supplier's products
  const supplierProducts = hotProducts.filter(p => p.supplier === supplier.name);

  const [activeTab, setActiveTab] = useState('overview');

  const certifications = [
    { name: 'ISO 9001:2015', icon: '🏆', description: 'Quality Management System' },
    { name: 'ISO 14001:2015', icon: '🌿', description: 'Environmental Management' },
    { name: 'REACH Compliance', icon: '✅', description: 'EU Chemical Regulation' },
    { name: 'GMP Certified', icon: '💊', description: 'Good Manufacturing Practice' }
  ];

  const stats = [
    { label: 'Years in Business', value: '15+' },
    { label: 'Products', value: '250+' },
    { label: 'Countries Served', value: '45+' },
    { label: 'Response Time', value: supplier.responseTime }
  ];

  const handleContact = () => {
    openContactForm(supplier.name);
  };

  const handleAddToCart = (product) => {
    addToCart(product, 100);
  };

  const handleToggleFavorite = (product, e) => {
    e.stopPropagation();
    toggleFavorite(product);
  };

  return (
    <div className="supplier-profile-page">
      <div className="container">
        {/* Back Button */}
        <button className="back-btn" onClick={() => navigate('/suppliers')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Suppliers
        </button>

        {/* Hero Section */}
        <div className="supplier-hero">
          <div className="hero-image">
            <img src={supplier.image} alt={supplier.name} />
            <div className="image-overlay"></div>
          </div>
          <div className="hero-content">
            <div className="supplier-badges">
              {supplier.verified && (
                <span className="badge badge-verified">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Verified Supplier
                </span>
              )}
              <span className="badge badge-premium">Premium Partner</span>
            </div>
            <h1 className="supplier-name">{supplier.name}</h1>
            <p className="supplier-location">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {supplier.location}
            </p>
            <div className="supplier-rating">
              <div className="stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill={star <= Math.round(supplier.rating) ? '#fbbf24' : 'none'}
                    stroke="#fbbf24"
                    strokeWidth="2"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <span className="rating-value">{supplier.rating}</span>
              <span className="rating-count">(127 reviews)</span>
            </div>
            <div className="hero-actions">
              <button className="btn btn-primary btn-lg" onClick={handleContact}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Contact Supplier
              </button>
              <button className="btn btn-outline btn-lg">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                </svg>
                Save to Favorites
              </button>
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="stats-bar">
          {stats.map((stat, i) => (
            <div key={i} className="stat-item">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`tab ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => setActiveTab('products')}
            >
              Products ({supplierProducts.length})
            </button>
            <button
              className={`tab ${activeTab === 'certifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('certifications')}
            >
              Certifications
            </button>
            <button
              className={`tab ${activeTab === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveTab('reviews')}
            >
              Reviews
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-content">
              <div className="content-grid">
                <div className="about-section">
                  <h2>About {supplier.name}</h2>
                  <p>
                    {supplier.name} is a leading chemical manufacturer and supplier based in {supplier.location}.
                    With over 15 years of experience in the industry, we specialize in providing high-quality
                    {supplier.specialties.join(', ')} to customers worldwide.
                  </p>
                  <p>
                    Our state-of-the-art manufacturing facilities and rigorous quality control processes ensure
                    that every product meets the highest international standards. We are committed to sustainable
                    and responsible chemical manufacturing practices.
                  </p>
                </div>

                <div className="specialties-section">
                  <h3>Specialties</h3>
                  <div className="specialty-tags">
                    {supplier.specialties.map((spec, i) => (
                      <span key={i} className="specialty-tag">{spec}</span>
                    ))}
                  </div>
                </div>

                <div className="highlights-section">
                  <h3>Why Choose Us</h3>
                  <ul className="highlights-list">
                    <li>
                      <span className="highlight-icon">✓</span>
                      <span>Verified and audited supplier</span>
                    </li>
                    <li>
                      <span className="highlight-icon">⚡</span>
                      <span>Fast response time ({supplier.responseTime})</span>
                    </li>
                    <li>
                      <span className="highlight-icon">🏆</span>
                      <span>ISO certified manufacturing</span>
                    </li>
                    <li>
                      <span className="highlight-icon">🌍</span>
                      <span>Global shipping available</span>
                    </li>
                    <li>
                      <span className="highlight-icon">📋</span>
                      <span>Full documentation & COA provided</span>
                    </li>
                    <li>
                      <span className="highlight-icon">💰</span>
                      <span>Competitive pricing</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'products' && (
            <div className="products-content">
              <h2>Products from {supplier.name}</h2>
              {supplierProducts.length > 0 ? (
                <div className="products-grid">
                  {supplierProducts.map((product) => (
                    <div key={product.id} className="product-card">
                      <div className="product-image">
                        <img src={product.image} alt={product.name} />
                        <span className="category-badge">{product.category}</span>
                      </div>
                      <div className="product-info">
                        <h4>{product.name}</h4>
                        <p className="cas-number">CAS: {product.casNumber}</p>
                        <div className="product-meta">
                          <span>Purity: {product.purity}</span>
                        </div>
                        <div className="product-actions">
                          <button
                            className="btn btn-sm btn-outline"
                            onClick={(e) => handleToggleFavorite(product, e)}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill={isFavorite(product.id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 7.78z" />
                            </svg>
                          </button>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => handleAddToCart(product)}
                          >
                            Add to Cart
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-products">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 7l-8-4-5.5 6.7-5.5-3.4V6m6.7 2.3V2m6.7-2.3v2m8-4 2.3" />
                  </svg>
                  <p>No products listed for this supplier yet.</p>
                  <button className="btn btn-outline" onClick={handleContact}>
                    Request Product Catalog
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'certifications' && (
            <div className="certifications-content">
              <h2>Certifications & Compliance</h2>
              <div className="certifications-grid">
                {certifications.map((cert, i) => (
                  <div key={i} className="cert-card">
                    <div className="cert-icon">{cert.icon}</div>
                    <div className="cert-info">
                      <h4>{cert.name}</h4>
                      <p>{cert.description}</p>
                      <span className="cert-status">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Verified
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="compliance-section">
                <h3>Regulatory Compliance</h3>
                <div className="compliance-list">
                  <div className="compliance-item">
                    <span className="check">✓</span>
                    <span>REACH (EU) Compliant</span>
                  </div>
                  <div className="compliance-item">
                    <span className="check">✓</span>
                    <span>TSCA (USA) Registered</span>
                  </div>
                  <div className="compliance-item">
                    <span className="check">✓</span>
                    <span>IECSC (China) Listed</span>
                  </div>
                  <div className="compliance-item">
                    <span className="check">✓</span>
                    <span>CSCL (India) Approved</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="reviews-content">
              <h2>Customer Reviews</h2>
              <div className="reviews-summary">
                <div className="summary-rating">
                  <span className="big-rating">{supplier.rating}</span>
                  <div className="stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <svg
                        key={star}
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill={star <= Math.round(supplier.rating) ? '#fbbf24' : 'none'}
                        stroke="#fbbf24"
                        strokeWidth="2"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>
                  <span className="total-reviews">Based on 127 reviews</span>
                </div>
                <div className="rating-bars">
                  {[5, 4, 3, 2, 1].map((stars) => (
                    <div key={stars} className="rating-bar">
                      <span>{stars} star</span>
                      <div className="bar">
                        <div className="bar-fill" style={{ width: `${stars === 5 ? 75 : stars === 4 ? 18 : stars === 3 ? 5 : 2}%` }}></div>
                      </div>
                      <span>{stars === 5 ? 95 : stars === 4 ? 23 : stars === 3 ? 6 : 3}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="reviews-list">
                {[
                  { author: 'Chemical Corp A', rating: 5, date: '2 weeks ago', comment: 'Excellent quality products and fast delivery. Very professional team.' },
                  { author: 'Industrial Solutions B', rating: 5, date: '1 month ago', comment: 'Great supplier with competitive pricing. Documentation is always complete and accurate.' },
                  { author: 'Pharma Tech C', rating: 4, date: '2 months ago', comment: 'Good quality products. Response time could be slightly faster but overall satisfied.' }
                ].map((review, i) => (
                  <div key={i} className="review-card">
                    <div className="review-header">
                      <div className="reviewer-info">
                        <div className="avatar">{review.author.charAt(0)}</div>
                        <div>
                          <span className="reviewer-name">{review.author}</span>
                          <span className="review-date">{review.date}</span>
                        </div>
                      </div>
                      <div className="review-rating">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <svg
                            key={star}
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill={star <= review.rating ? '#fbbf24' : 'none'}
                            stroke="#fbbf24"
                            strokeWidth="2"
                          >
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                    <p className="review-comment">{review.comment}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Contact CTA */}
        <div className="contact-cta">
          <div className="cta-content">
            <h2>Interested in working with {supplier.name}?</h2>
            <p>Get in touch for quotes, product inquiries, or partnership opportunities</p>
          </div>
          <button className="btn btn-accent btn-lg" onClick={handleContact}>
            Contact Supplier
          </button>
        </div>
      </div>

      <style jsx>{`
        .supplier-profile-page {
          padding: var(--space-xl) 0 var(--space-3xl);
          min-height: 100vh;
        }

        .back-btn {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          background: none;
          border: none;
          color: var(--color-neutral-400);
          font-size: var(--font-sm);
          cursor: pointer;
          padding: var(--space-sm) 0;
          margin-bottom: var(--space-lg);
          transition: color var(--transition-fast);
        }

        .back-btn:hover {
          color: var(--color-accent-400);
        }

        .supplier-hero {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: var(--space-xl);
          padding: var(--space-xl);
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-xl);
          margin-bottom: var(--space-xl);
        }

        .hero-image {
          position: relative;
          height: 250px;
          border-radius: var(--radius-lg);
          overflow: hidden;
        }

        .hero-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .image-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, transparent 50%, rgba(0, 0, 0, 0.5) 100%);
        }

        .hero-content {
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

        .supplier-badges {
          display: flex;
          gap: var(--space-sm);
          margin-bottom: var(--space-md);
        }

        .badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 10px;
          border-radius: var(--radius-sm);
          font-size: var(--font-xs);
          font-weight: 500;
        }

        .badge-verified {
          background: rgba(16, 185, 129, 0.2);
          color: var(--color-success);
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .badge-premium {
          background: rgba(59, 130, 246, 0.2);
          color: var(--color-accent-400);
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .supplier-name {
          font-size: var(--font-3xl);
          font-weight: 700;
          color: var(--color-neutral-50);
          margin-bottom: var(--space-sm);
        }

        .supplier-location {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          color: var(--color-neutral-400);
          margin-bottom: var(--space-md);
        }

        .supplier-rating {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          margin-bottom: var(--space-lg);
        }

        .stars {
          display: flex;
          gap: 2px;
        }

        .rating-value {
          font-weight: 600;
          color: var(--color-neutral-100);
        }

        .rating-count {
          color: var(--color-neutral-500);
          font-size: var(--font-sm);
        }

        .hero-actions {
          display: flex;
          gap: var(--space-md);
        }

        .btn-lg {
          padding: var(--space-md) var(--space-xl);
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }

        .stats-bar {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: var(--space-md);
          margin-bottom: var(--space-xl);
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: var(--space-lg);
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-lg);
        }

        .stat-value {
          font-size: var(--font-2xl);
          font-weight: 700;
          color: var(--color-accent-400);
        }

        .stat-label {
          font-size: var(--font-xs);
          color: var(--color-neutral-500);
          text-transform: uppercase;
        }

        .tabs-container {
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          margin-bottom: var(--space-xl);
        }

        .tabs {
          display: flex;
          gap: var(--space-xs);
        }

        .tab {
          padding: var(--space-md) var(--space-lg);
          background: none;
          border: none;
          color: var(--color-neutral-400);
          font-size: var(--font-sm);
          font-weight: 500;
          cursor: pointer;
          position: relative;
          transition: color var(--transition-fast);
        }

        .tab:hover {
          color: var(--color-neutral-200);
        }

        .tab.active {
          color: var(--color-accent-400);
        }

        .tab.active::after {
          content: '';
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
          height: 2px;
          background: var(--color-accent-400);
        }

        .tab-content {
          min-height: 400px;
        }

        .content-grid {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: var(--space-xl);
        }

        .about-section h2,
        .specialties-section h3,
        .highlights-section h3 {
          font-size: var(--font-xl);
          color: var(--color-neutral-100);
          margin-bottom: var(--space-md);
        }

        .about-section p {
          color: var(--color-neutral-400);
          line-height: 1.8;
          margin-bottom: var(--space-md);
        }

        .specialty-tags {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-sm);
        }

        .specialty-tag {
          padding: var(--space-sm) var(--space-md);
          background: rgba(59, 130, 246, 0.15);
          color: var(--color-accent-300);
          border-radius: var(--radius-md);
          font-size: var(--font-sm);
        }

        .highlights-list {
          list-style: none;
          padding: 0;
        }

        .highlights-list li {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: var(--space-sm) 0;
          color: var(--color-neutral-300);
        }

        .highlight-icon {
          color: var(--color-success);
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: var(--space-lg);
        }

        .product-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-lg);
          overflow: hidden;
          transition: all var(--transition-base);
        }

        .product-card:hover {
          border-color: var(--color-accent-400);
          transform: translateY(-2px);
        }

        .product-card .product-image {
          height: 140px;
          position: relative;
          background: rgba(0, 0, 0, 0.3);
        }

        .product-card .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .product-card .category-badge {
          position: absolute;
          top: var(--space-sm);
          left: var(--space-sm);
          padding: 4px 8px;
          background: rgba(59, 130, 246, 0.9);
          color: white;
          font-size: var(--font-xs);
          border-radius: var(--radius-sm);
        }

        .product-card .product-info {
          padding: var(--space-md);
        }

        .product-card h4 {
          color: var(--color-neutral-100);
          margin-bottom: var(--space-xs);
        }

        .product-card .cas-number {
          font-size: var(--font-xs);
          color: var(--color-neutral-500);
          font-family: var(--font-mono);
          margin-bottom: var(--space-sm);
        }

        .product-card .product-meta {
          display: flex;
          justify-content: space-between;
          margin-bottom: var(--space-sm);
          font-size: var(--font-sm);
          color: var(--color-neutral-400);
        }

        .product-card .price {
          color: var(--color-accent-300);
          font-weight: 600;
        }

        .product-card .product-actions {
          display: flex;
          gap: var(--space-sm);
        }

        .certifications-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-lg);
          margin-bottom: var(--space-xl);
        }

        .cert-card {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          padding: var(--space-lg);
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: var(--radius-lg);
        }

        .cert-icon {
          font-size: 2rem;
        }

        .cert-info h4 {
          color: var(--color-neutral-100);
          margin-bottom: var(--space-xs);
        }

        .cert-info p {
          color: var(--color-neutral-400);
          font-size: var(--font-sm);
          margin-bottom: var(--space-xs);
        }

        .cert-status {
          display: flex;
          align-items: center;
          gap: 4px;
          color: var(--color-success);
          font-size: var(--font-sm);
        }

        .compliance-section {
          padding: var(--space-lg);
          background: rgba(255, 255, 255, 0.03);
          border-radius: var(--radius-lg);
        }

        .compliance-section h3 {
          margin-bottom: var(--space-md);
        }

        .compliance-list {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-sm);
        }

        .compliance-item {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          color: var(--color-neutral-300);
        }

        .compliance-item .check {
          color: var(--color-success);
        }

        .reviews-summary {
          display: grid;
          grid-template-columns: 250px 1fr;
          gap: var(--space-xl);
          margin-bottom: var(--space-xl);
          padding: var(--space-xl);
          background: rgba(255, 255, 255, 0.03);
          border-radius: var(--radius-lg);
        }

        .summary-rating {
          text-align: center;
        }

        .big-rating {
          display: block;
          font-size: 3rem;
          font-weight: 700;
          color: var(--color-neutral-100);
        }

        .total-reviews {
          font-size: var(--font-xs);
          color: var(--color-neutral-500);
        }

        .rating-bars {
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
        }

        .rating-bar {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          font-size: var(--font-sm);
          color: var(--color-neutral-400);
        }

        .rating-bar .bar {
          flex: 1;
          height: 8px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }

        .rating-bar .bar-fill {
          height: 100%;
          background: #fbbf24;
          border-radius: 4px;
        }

        .reviews-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
        }

        .review-card {
          padding: var(--space-lg);
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: var(--radius-lg);
        }

        .review-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-sm);
        }

        .reviewer-info {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
        }

        .avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, var(--color-accent-500), var(--color-accent-300));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 600;
        }

        .reviewer-name {
          display: block;
          color: var(--color-neutral-100);
          font-weight: 500;
        }

        .review-date {
          display: block;
          font-size: var(--font-xs);
          color: var(--color-neutral-500);
        }

        .review-comment {
          color: var(--color-neutral-300);
          line-height: 1.6;
        }

        .contact-cta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-xl);
          margin-top: var(--space-2xl);
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(6, 182, 212, 0.2));
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: var(--radius-xl);
        }

        .contact-cta h2 {
          font-size: var(--font-xl);
          color: var(--color-neutral-50);
          margin-bottom: var(--space-xs);
        }

        .contact-cta p {
          color: var(--color-neutral-400);
        }

        .btn-accent {
          background: linear-gradient(135deg, var(--color-accent-300), var(--color-accent-400));
          color: var(--color-neutral-900);
          font-weight: 600;
        }

        .no-products {
          text-align: center;
          padding: var(--space-2xl);
          color: var(--color-neutral-400);
        }

        .not-found-page {
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .not-found-content {
          text-align: center;
        }

        .not-found-content h1 {
          font-size: var(--font-2xl);
          color: var(--color-neutral-100);
          margin: var(--space-lg) 0 var(--space-sm);
        }

        .not-found-content p {
          color: var(--color-neutral-400);
          margin-bottom: var(--space-lg);
        }

        @media (max-width: 1024px) {
          .supplier-hero {
            grid-template-columns: 1fr;
          }

          .hero-image {
            height: 200px;
          }

          .stats-bar {
            grid-template-columns: repeat(2, 1fr);
          }

          .content-grid {
            grid-template-columns: 1fr;
          }

          .certifications-grid {
            grid-template-columns: 1fr;
          }

          .reviews-summary {
            grid-template-columns: 1fr;
            text-align: center;
          }
        }

        @media (max-width: 768px) {
          .hero-actions {
            flex-direction: column;
          }

          .contact-cta {
            flex-direction: column;
            text-align: center;
            gap: var(--space-lg);
          }
        }
      `}</style>
    </div>
  );
};

export default SupplierProfilePage;
